// @flow
import { useMemo } from 'react';
import moment from 'moment';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import useFormatMessage from '../../hooks/useFormatMessage';
import {
  PLANNING_RANGE_BUCKETS,
  RECERT_DUE_FILTER_VALUES,
  getRecertDuePropertyPathEntry,
} from './constantsV2';
import messages from './messages';

const LABEL_MESSAGE_KEY_BY_BUCKET = {
  [RECERT_DUE_FILTER_VALUES.LATE]: 'planningRangePastDue',
  [RECERT_DUE_FILTER_VALUES.UNDER_30]: 'planningRange0To30',
  [RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60]: 'planningRange31To60',
  [RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90]: 'planningRange61To90',
  [RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120]: 'planningRange91To120',
  [RECERT_DUE_FILTER_VALUES.UPCOMING]: 'planningRange120Plus',
};

// Mirrors the tooltip/description composition style used by
// AllManageLeaseExpirations/hooks.js's `useCalculateMetrics` (a base
// message plus "on or before" / "between ... and ..." plus formatted
// date(s)) — but sources the day-range boundaries from this screen's own
// existing recert-due bucket definitions (`recertDueDateFilterFn`) rather
// than re-deriving them, so the tooltip text can never drift from the
// actual filtering behavior.
const useBucketTooltips = (formatMessage) => {
  return useMemo(() => {
    const today = moment().startOf('day');
    const base = formatMessage(messages.planningRangeTooltipBase);
    const onOrBefore = formatMessage(messages.planningRangeTooltipOnOrBefore);
    const between = formatMessage(messages.planningRangeTooltipBetween);
    const and = formatMessage(messages.planningRangeTooltipAnd);
    const after = formatMessage(messages.planningRangeTooltipAfter);

    const dateAt = (daysFromToday) =>
      formatDateDisplayLocal(today.clone().add(daysFromToday, 'days'));

    return {
      [RECERT_DUE_FILTER_VALUES.LATE]: `${base} ${onOrBefore} ${dateAt(-1)}`,
      [RECERT_DUE_FILTER_VALUES.UNDER_30]: `${base} ${between} ${dateAt(
        0,
      )} ${and} ${dateAt(30)}`,
      [RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60]: `${base} ${between} ${dateAt(
        31,
      )} ${and} ${dateAt(60)}`,
      [RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90]: `${base} ${between} ${dateAt(
        61,
      )} ${and} ${dateAt(90)}`,
      [RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120]: `${base} ${between} ${dateAt(
        91,
      )} ${and} ${dateAt(120)}`,
      [RECERT_DUE_FILTER_VALUES.UPCOMING]: `${base} ${after} ${dateAt(120)}`,
    };
  }, [formatMessage]);
};

/**
 * Computes live per-bucket counts for the Recerts tab's `PlanningRangeFilter`
 * cards, without reimplementing any bucket/date filtering logic: for each
 * bucket, it calls the EXACT same `filterFn` that already powers the
 * existing "Recert Due" column header filter dropdown
 * (`getRecertDuePropertyPathEntry`), against `baselineResults` (the results
 * with every OTHER currently-active filter already applied, but NOT
 * `recertDue` itself). Because counts are derived from a `recertDue`-free
 * baseline, they stay accurate/live no matter how many buckets are
 * currently selected (an unselected bucket's count never zeroes out, and a
 * selected bucket's count doesn't shrink just because other buckets are
 * also selected).
 */
const usePlanningRangeFilter = ({
  baselineResults,
  propertyAffordablePrograms,
}: {
  baselineResults: Array<Object>,
  propertyAffordablePrograms: Array<string>,
}) => {
  const formatMessage = useFormatMessage();
  const tooltips = useBucketTooltips(formatMessage);

  return useMemo(() => {
    const { path, filterPath, filterFn } = getRecertDuePropertyPathEntry();
    const valuePath = filterPath ?? path;
    const items = Array.isArray(baselineResults) ? baselineResults : [];
    const programs = Array.isArray(propertyAffordablePrograms)
      ? propertyAffordablePrograms
      : [];

    return PLANNING_RANGE_BUCKETS.map((bucketId) => {
      const bucketItems = items.filter((row) =>
        filterFn([bucketId], row?.[valuePath]),
      );

      // Per-program breakdown is derived from the SAME already-filtered
      // `bucketItems` the total count comes from (grouped by `programName`)
      // rather than a separately-computed server aggregate, so the two
      // numbers can never disagree - e.g. a cert whose status excludes it
      // from the live bucket count (completed/reclassified certs) is
      // excluded from its program's count too.
      const breakdown = programs.map((program) => ({
        label: program,
        count: bucketItems.filter((row) => row?.programName === program).length,
      }));

      return {
        id: bucketId,
        label: formatMessage(messages[LABEL_MESSAGE_KEY_BY_BUCKET[bucketId]]),
        count: bucketItems.length,
        tooltip: tooltips[bucketId],
        breakdown,
      };
    });
  }, [baselineResults, formatMessage, tooltips, propertyAffordablePrograms]);
};

export default usePlanningRangeFilter;
